/**
 * Creative tag code generation — the ad tags handed to third-party systems.
 *
 * A port of the tag builder in the old UI (`creative-defaults.ts`), so the code
 * these downloads produce is byte-for-byte what that UI produced.
 */

const API_BASE = import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'

/** The delivery host matching the API environment the app is pointed at. */
export function adServerHost(): string {
  if (API_BASE.includes('localhost')) return 'http://localhost:8078'
  if (API_BASE.includes('api.dev.')) return 'https://adserver.dev.adnuntius.com'
  if (API_BASE.includes('api.staging.')) return 'https://adserver.staging.adnuntius.com'
  return 'https://delivery.adnuntius.com'
}

const deliveryUri = (path: string) => `${adServerHost()}/${path}`

export type TagFormat =
  | 'STANDARD_JAVASCRIPT'
  | 'GOOGLE_AD_MANAGER'
  | 'GOOGLE_CAMPAIGN_MANAGER'
  | 'DV360'
  | 'ADFORM'
  | 'XANDR'

export interface TagFormatOption {
  id: TagFormat
  label: string
  /** Whether the format has a spreadsheet trafficking sheet to download. */
  xls: boolean
}

export const TAG_FORMATS: TagFormatOption[] = [
  { id: 'STANDARD_JAVASCRIPT', label: 'Standard JavaScript', xls: false },
  { id: 'GOOGLE_AD_MANAGER', label: 'Google Ad Manager', xls: false },
  { id: 'GOOGLE_CAMPAIGN_MANAGER', label: 'Google Campaign Manager', xls: true },
  { id: 'DV360', label: 'DV360', xls: false },
  { id: 'ADFORM', label: 'Adform', xls: true },
  { id: 'XANDR', label: 'Xandr', xls: false },
]

/** What the tags point at: the parent object serving them, and the network. */
export interface TagContext {
  /** Which parent field the tag requests by — `lineItemId` for a line item. */
  parentType: 'lineItemId' | 'orderId' | 'creativeSetId'
  parentId: string
  /** The network's tag ID, not its UUID. */
  networkTagId: string
  adUnitId?: string
  useCookies?: boolean
  /** Extra sizes the tag may fill, as [width, height] pairs. */
  dimensions?: [number, number][]
  clickTrackingUrl?: string
  targeting?: boolean
}

/** The creative fields a tag needs — anything with an id and a size fits. */
export interface TagCreative {
  id: string
  name: string
  width?: number
  height?: number
}

const CREATIVE_TAG_REPL = '/*CREATIVETAG*/'

const DEFAULT_STUFF = 'auId=[adUnitId]&noCookies=&[parentType]=[parentId]&creativeId=[creativeId]&network=[networkId]'
const FORMAT_IMAGE_SUFFIX = `${DEFAULT_STUFF}&format=image&cb=[CACHEBUSTER]`
const TRACKER_SUFFIX = `${DEFAULT_STUFF}&cb=[CACHEBUSTER]`
const FORMAT_CLICK_SUFFIX = `${DEFAULT_STUFF}&r=`

const JS_CALL =
  '\x3Cdiv id="[targetId]">\x3C/div>\n\x3Cscript src="https://cdn.adnuntius.com/adn.js" async>\x3C/script>\n\x3Cscript>\nwindow.adn = window.adn || {}; adn.calls = adn.calls || [];\nadn.calls.push(function() {\n   ' +
  'adn.request({ [parentType]: "[parentId]", targetId: "[targetId]", ' +
  CREATIVE_TAG_REPL +
  'network: "[networkId]" });\n});\n\x3C/script>'

interface ReplaceOptions extends TagContext {
  creative?: TagCreative | null
  destinationUrl?: string
  targetId?: string
  auW?: number
  auH?: number
  /** Requests the cookie-less, iframe-safe delivery environment. */
  limited?: boolean
  extras?: string
  gdpr?: string
  gdprConsent?: string
}

function replace(code: string, o: ReplaceOptions): string {
  const { creative, adUnitId = '', networkTagId, parentType, parentId } = o
  const useCookies = o.useCookies !== false
  let out = code

  out = useCookies ? out.replace(/noCookies=&/g, '') : out.replace(/noCookies=/g, 'noCookies=true')
  out = out.replace(/\[parentType]/g, parentType).replace(/\[parentId]/g, parentId)

  if (creative?.id) {
    out = out.replace(/\[creativeId]/g, creative.id)
  } else {
    out = out.replace('creativeId=[creativeId]&', '')
  }

  const width = o.auW || creative?.width
  const height = o.auH || creative?.height
  out = width ? out.replace(/\[width]/g, String(width)) : out.replace('width="[width]" ', '')
  out = height ? out.replace(/\[height]/g, String(height)) : out.replace('height="[height]" ', '')
  if (o.targetId) out = out.replace(/\[targetId]/g, o.targetId)

  const sizes = (o.dimensions ?? []).filter((d) => Number.isFinite(d[0]) && Number.isFinite(d[1]))
  const dimensionsReplace = sizes.length > 0 ? `[${sizes.map((d) => `[${d[0]},${d[1]}]`).join(',')}]` : ''

  if (out.includes(CREATIVE_TAG_REPL)) {
    let data = ''
    if (o.limited) data += 'env: "limited", '
    if (!useCookies) data += 'useCookies: false, '
    if (adUnitId) data += `auId: "${adUnitId}", `
    if (creative) data += `creativeId: "${creative.id}", `
    if (width && width > 0) data += `auW: ${width}, `
    if (height && height > 0) data += `auH: ${height}, `
    if (dimensionsReplace) data += `dimensions: ${dimensionsReplace}, `
    if (o.clickTrackingUrl) data += `clickTrackingUrl: "${o.clickTrackingUrl}", `
    if (o.targeting) data += 'targeting: true, '
    if (o.gdpr) data += `gdpr: ${o.gdpr}, `
    if (o.gdprConsent) data += `consentString: ${o.gdprConsent}, `
    if (o.extras) data += o.extras
    out = out.replace(CREATIVE_TAG_REPL, data)
  } else {
    out = out.replace('[targeting]', o.targeting ? 'targeting=true&' : '')
    out = out.replace('[dimensions]', `dimensions=${dimensionsReplace}&`)
  }

  if (o.clickTrackingUrl) out = out.replace('clickTrackingUrl="', `clickTrackingUrl=${o.clickTrackingUrl}"`)
  out = out.replace(/\[adUnitId]/g, adUnitId).replace(/\[networkId]/g, networkTagId || '')
  if (o.destinationUrl) out = out.replace(/\[destinationUrl]/g, encodeURIComponent(o.destinationUrl))

  return out.replace('?auId=&', '?').replace('&auId=&', '&').replace('auId=&', '')
}

/** Impression-counting pixel for insertion into third-party systems. */
export function impressionTracker(ctx: TagContext, creative?: TagCreative | null): string {
  return replace(`${deliveryUri('pixeli.gif')}?${TRACKER_SUFFIX}`, { ...ctx, creative })
}

/** Click tracker for insertion into third-party systems. */
export function clickTracker(ctx: TagContext, creative?: TagCreative | null): string {
  return replace(`${deliveryUri('c')}?${FORMAT_CLICK_SUFFIX}[ESCAPED_CLICK_URL]`, { ...ctx, creative })
}

/** The image-and-link markup for pasting into an email. */
export function emailTag(ctx: TagContext, creative: TagCreative, destinationUrl: string): string {
  return replace(
    `<a rel="nofollow" href="${deliveryUri('c')}?${FORMAT_CLICK_SUFFIX}[destinationUrl]"><img\n    src="${deliveryUri('i')}?${FORMAT_IMAGE_SUFFIX}"\nalt=""></a>`,
    { ...ctx, creative, destinationUrl }
  )
}

/** The tag for one creative in the given third-party format. */
export function creativeTag(format: TagFormat, ctx: TagContext, creative: TagCreative, targetId: string): string {
  const base: ReplaceOptions = { ...ctx, creative, targetId, auW: creative.width, auH: creative.height }
  switch (format) {
    case 'STANDARD_JAVASCRIPT':
      return replace(JS_CALL, base)
    case 'GOOGLE_AD_MANAGER':
    case 'DV360':
      return replace(JS_CALL, {
        ...base,
        limited: true,
        clickTrackingUrl: '%%CLICK_URL_ESC%%',
        gdpr: '"${GDPR}"',
        gdprConsent: '"${GDPR_CONSENT}"',
      })
    case 'GOOGLE_CAMPAIGN_MANAGER':
      return replace(JS_CALL, {
        ...base,
        limited: true,
        clickTrackingUrl: '%c%u',
        extras: "kv: { source: ['cm360'] }, ",
        gdpr: '"%%GDPR%%"',
        gdprConsent: '"%%GDPR_CONSENT%%"',
      })
    case 'ADFORM':
      return replace(JS_CALL, { ...base, limited: true, clickTrackingUrl: '%%c1;cpdir=' })
    case 'XANDR':
      return replace(JS_CALL, {
        ...base,
        limited: true,
        clickTrackingUrl: '${CLICK_URL}',
        gdpr: '"${GDPR_APPLIES}"',
        gdprConsent: '"${GDPR_CONSENT_STRING}"',
      })
  }
}

/**
 * The direct `<script>` version of the standard tag — no adn.js, the ad server
 * writes the creative straight into the page.
 */
export function scriptServeTag(ctx: TagContext, creative: TagCreative): string {
  return replace(
    `<script src="${deliveryUri('i')}?${DEFAULT_STUFF}&format=js&[targeting][dimensions]cb=[CACHEBUSTER]&clickTrackingUrl=" async></script>`,
    { ...ctx, creative }
  )
}

/** The direct `<script>` version for Google Ad Manager, on limited delivery. */
export function scriptServeGamTag(ctx: TagContext, creative: TagCreative): string {
  const uri = deliveryUri('i').replace('//delivery', '//limited.delivery')
  return replace(
    `<script src="${uri}?${DEFAULT_STUFF}&format=js&[targeting][dimensions]cb=%%CACHEBUSTER%%&clickTrackingUrl=%%CLICK_URL_ESC%%" async></script>`,
    { ...ctx, creative }
  )
}

/** The iframe version of the standard tag. */
export function iframeTag(ctx: TagContext, creative: TagCreative): string {
  return replace(
    `<iframe class="adnThirdPartyCreative" src="${deliveryUri('i')}?${DEFAULT_STUFF}&[targeting][dimensions]cb=[CACHEBUSTER]&clickTrackingUrl=" width="[width]" height="[height]" style="padding: 0; border: 0;"></iframe>`,
    { ...ctx, creative, auW: creative.width, auH: creative.height }
  )
}

/**
 * The VAST URL a video player requests for one creative. The ad unit's VAST URL
 * names the ad unit; this one names the creative and the line item it serves
 * through, so the player gets that creative specifically.
 */
export function creativeVastTag(
  ctx: Pick<TagContext, 'networkTagId' | 'parentId'>,
  creative: TagCreative,
  vastVersion: string
): string {
  const params = new URLSearchParams({
    network: ctx.networkTagId,
    creativeId: creative.id,
    lineItemId: ctx.parentId,
    tt: vastVersion,
  })
  return `${deliveryUri('i')}?${params}`
}

/** One rendered tag, as the tag tab lists them. */
export interface CreativeTagCode {
  id: string
  /** i18n key suffix under `creativeTags.codes`. */
  titleKey: string
  code: string
}

/**
 * Every tag a format offers: the tag itself, plus the alternative forms the
 * standard and Google Ad Manager formats also provide.
 */
export function creativeTagCodes(
  format: TagFormat,
  ctx: TagContext,
  creative: TagCreative,
  targetId: string
): CreativeTagCode[] {
  const codes: CreativeTagCode[] = [
    { id: 'primary', titleKey: format, code: creativeTag(format, ctx, creative, targetId) },
  ]
  if (format === 'STANDARD_JAVASCRIPT') {
    codes.push(
      { id: 'script', titleKey: 'standardScript', code: scriptServeTag(ctx, creative) },
      { id: 'iframe', titleKey: 'standardIframe', code: iframeTag(ctx, creative) }
    )
  }
  if (format === 'GOOGLE_AD_MANAGER') {
    codes.push({ id: 'script', titleKey: 'gamScript', code: scriptServeGamTag(ctx, creative) })
  }
  return codes
}

/** Formats that only serve "safe" creatives, which the tag tab warns about. */
export const LIMITED_FORMATS: TagFormat[] = ['GOOGLE_AD_MANAGER', 'DV360', 'GOOGLE_CAMPAIGN_MANAGER']

/** A self-contained HTML page serving the creative — one file per creative. */
export function zippedHtml(ctx: TagContext, creative: TagCreative, targetId: string): string {
  const width = creative.width ?? ''
  const height = creative.height ?? ''
  return `<html>

<head>
	<meta name="ad.size" content="width=${width},height=${height}" />
</head>

<body style="margin:0px">
	<div id="${targetId}"></div>
	<script src="https://cdn.adnuntius.com/adn.js" async></script>

	<script>
		var clickTag = "";
	</script>

	<script>
        var adLoaded = false;
		window.adn = window.adn || {};
		adn.calls = adn.calls || [];
		var loadad = function () {
            if (!adLoaded) {
                adn.calls.push(function () {
                    adn.request({
                        targeting: true,
                        ${ctx.parentType}: "${ctx.parentId}",
                        targetId: "${targetId}",
                        network: "${ctx.networkTagId}",
                        dimensions: [[${width}, ${height}]],
                        clickTrackingUrl: clickTag,
                        consentString: "\${GDPR_CONSENT_855}",
                        gdpr: "\${GDPR}",
                        useCookies: false,
                        env: "limited"
                    });
                });
                adLoaded = true;
            }
		};

		function inIframe() {
			try {
				return window.self !== window.top;
			} catch (e) {
				return true;
			}
		}

		if (inIframe()) {
			window.addEventListener('message', function (event) {
				loadad()
			});
		} else {
			loadad()
		}

	</script>
</body>

</html>`
}

/** A short, stable-per-download element id for the tag's target div. */
export function generateTargetId(): string {
  return `adn-${crypto.randomUUID().replace(/-/g, '').slice(0, 6)}`
}
